import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, Crown, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchDonors, fetchDonationStats, type DonorRow, type DonationStats } from '@/lib/donations';

export default function Donors() {
  const { t } = useTranslation();
  const [donors, setDonors] = useState<DonorRow[]>([]);
  const [stats, setStats] = useState<DonationStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchDonors(100), fetchDonationStats()])
      .then(([d, s]) => { setDonors(d); setStats(s); })
      .catch(() => { /* mock fallback */ })
      .finally(() => setLoading(false));
  }, []);

  const totalEur = stats ? stats.donations.total_cents / 100 : 0;
  const subsActive = stats?.active_subscriptions.reduce((sum, s) => sum + s.n, 0) ?? 0;

  return (
    <>
      <section className="section pb-12">
        <div className="container-narrow text-center">
          <h1 className="font-pixel text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight">
            {t('donors.title')}
          </h1>
          <p className="mt-4 text-kquest-300 text-lg max-w-2xl mx-auto">
            {t('donors.subtitle')}
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
            <Stat label={t('donors.stat_total')} value={`€${totalEur.toLocaleString('it-IT')}`} accent="gold" />
            <Stat label={t('donors.stat_donors')} value={`${stats?.donations.donors_count ?? donors.length}`} accent="xp" />
            <Stat label={t('donors.stat_donations')} value={`${stats?.donations.donations_count ?? donors.length}`} accent="purple" />
            <Stat label={t('donors.stat_subs')} value={`${subsActive}`} accent="purple" />
          </div>

          <div className="mt-10">
            <Link to="/donate" className="btn-gold !px-8 !py-3">
              <Heart className="w-5 h-5" /> {t('donors.cta')}
            </Link>
          </div>
        </div>
      </section>

      <section className="section pt-0">
        <div className="container-narrow max-w-4xl">
          <h2 className="font-pixel text-2xl mb-6 flex items-center gap-3">
            <Crown className="w-7 h-7 text-gold-400" /> {t('donors.wall_title')}
          </h2>

          {loading ? (
            <div className="text-center text-kquest-300 py-10">Loading…</div>
          ) : donors.length === 0 ? (
            <div className="kblock p-12 text-center">
              <Heart className="w-12 h-12 text-gold-400 mx-auto mb-4" />
              <p className="font-pixel text-xl mb-2">{t('donors.empty_title')}</p>
              <p className="text-kquest-300 mb-6">{t('donors.empty_subtitle')}</p>
              <Link to="/donate" className="btn-gold !px-6 !py-3">
                <Heart className="w-4 h-4" /> {t('donors.empty_cta')}
              </Link>
            </div>
          ) : (
            <div className="grid gap-3">
              {donors.map((d, i) => (
                <DonorCard key={i} d={d} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent: 'gold'|'xp'|'purple' }) {
  const cls = accent === 'gold' ? 'text-gold-400' : accent === 'xp' ? 'text-xp-400' : 'text-kquest-200';
  return (
    <div className="kblock p-5 text-center">
      <div className={`font-press text-xl sm:text-2xl ${cls}`}>{value}</div>
      <div className="mt-2 text-[10px] sm:text-xs font-pixel uppercase tracking-wider text-kquest-400">{label}</div>
    </div>
  );
}

function DonorCard({ d, index }: { d: DonorRow; index: number }) {
  const eur = (d.amount_cents / 100).toFixed(2);
  const date = d.paid_at ? d.paid_at.slice(0, 10) : '—';
  const isAnonymous = d.anonymous === 1 || !d.display_name;
  const name = isAnonymous ? 'Anonimo' : d.yt_channel ?? d.display_name ?? 'Anonimo';

  return (
    <motion.article
      initial={{ opacity: 0, x: -8 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.3, delay: Math.min(index, 10) * 0.03 }}
      className="kblock p-5 flex items-center gap-4"
    >
      <div className="flex-shrink-0">
        {d.avatar && !isAnonymous ? (
          <img src={d.avatar} alt="" className="w-12 h-12 border-2 border-kquest-500" />
        ) : (
          <div className="w-12 h-12 bg-kquest-700 border-2 border-kquest-500 flex items-center justify-center font-pixel text-lg">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-pixel text-lg text-kquest-100 truncate">{name}</div>
        {d.message && (
          <div className="mt-1 text-sm text-kquest-300 italic flex items-start gap-1">
            <MessageSquare className="w-3 h-3 mt-1 flex-shrink-0 text-kquest-500" />
            <span className="line-clamp-2">"{d.message}"</span>
          </div>
        )}
      </div>
      <div className="flex-shrink-0 text-right">
        <div className="font-press text-lg text-gold-400">€{eur}</div>
        <div className="text-xs text-kquest-400 font-pixel">{date}</div>
      </div>
    </motion.article>
  );
}
